import React, { useState, useEffect, useCallback } from 'react';
import {
  getCoupons,
  getCouponDetail,
  createCoupon,
  toggleCouponActive,
  deleteCoupon,
  getCouponTypeOptions,
  getDiscountTypeOptions,
  getTargetTypeOptions,
  getCouponTypeLabel,
  getDiscountTypeLabel,
  getTargetTypeLabel,
} from '../services/couponService';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import Snackbar from '../components/common/Snackbar';
import TargetSelector from '../components/common/TargetSelector';
import useSnackbar from '../hooks/useSnackbar';
import '../css/CouponManagement.css';

const initialForm = {
  couponCode: '',
  name: '',
  description: '',
  couponType: 'EVENT',
  discountType: 'AMOUNT',
  discountValue: '',
  maxDiscountAmount: '',
  minOrderAmount: '',
  validFrom: '',
  validUntil: '',
  totalQuantity: '',
  targetType: 'ALL',
  targetItems: [],   // [{id, label, sub}]
  maxUsagePerCustomer: 1,
};

const CouponManagement = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, size: 20, totalElements: 0, totalPages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCoupons({
        page: pagination.page,
        size: pagination.size,
        search: searchKeyword,
      });
      setCoupons(data.content || []);
      setPagination((prev) => ({
        ...prev,
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
      }));
    } catch (error) {
      showSnackbar(error.message || '쿠폰 목록을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, searchKeyword, showSnackbar]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setSearchKeyword(searchInput.trim());
  };

  const handlePage = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleToggleActive = async (coupon) => {
    const nextActive = !coupon.isActive;
    if (!nextActive) {
      const confirmMsg = `쿠폰 [${coupon.couponCode}] 을(를) 비활성화하시겠습니까?\n\n` +
        `· 신규 발급이 차단됩니다.\n` +
        `· 이미 발급된 쿠폰도 사용이 즉시 차단됩니다.\n` +
        `· 다시 활성화하여 언제든 재개할 수 있습니다.\n\n` +
        `※ 어드민 목록에서 완전히 숨기고 영구 종료하려면\n` +
        `   쿠폰 상세보기 하단의 [쿠폰 삭제] 버튼을 이용하세요.`;
      if (!window.confirm(confirmMsg)) return;
    }
    try {
      await toggleCouponActive(coupon.couponId, nextActive);
      showSnackbar(`쿠폰 [${coupon.couponCode}] ${nextActive ? '활성화' : '비활성화'} 완료`, 'success');
      fetchCoupons();
    } catch (error) {
      showSnackbar(error.message || '상태 변경에 실패했습니다.', 'error');
    }
  };

  const openDetail = async (coupon) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const result = await getCouponDetail(coupon.couponId);
      setDetail(result?.data || null);
    } catch (error) {
      showSnackbar(error.message || '쿠폰 상세를 불러오지 못했습니다.', 'error');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteCoupon = async () => {
    if (!detail) return;
    const confirmMsg = `쿠폰 [${detail.couponCode}] 을(를) 삭제하시겠습니까?\n\n` +
      `· 어드민 목록에서 숨겨지며 되돌릴 수 없습니다.\n` +
      `· 신규 발급이 차단됩니다.\n` +
      `· 이미 발급된 쿠폰도 사용이 즉시 차단됩니다.\n\n` +
      `※ 신규 발급만 잠시 멈추고 나중에 재개하려면\n` +
      `   쿠폰 목록의 [비활성화] 버튼을 이용하세요.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await deleteCoupon(detail.couponId);
      showSnackbar(`쿠폰 [${detail.couponCode}] 삭제 완료`, 'success');
      setDetailOpen(false);
      fetchCoupons();
    } catch (error) {
      showSnackbar(error.message || '쿠폰 삭제에 실패했습니다.', 'error');
    }
  };

  const openCreate = () => {
    setForm(initialForm);
    setCreateOpen(true);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = () => {
    const targetIds = form.targetType === 'ALL'
      ? []
      : (form.targetItems || []).map((it) => it.id);
    return {
      couponCode: form.couponCode.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      couponType: form.couponType,
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      maxDiscountAmount: form.maxDiscountAmount ? Number(form.maxDiscountAmount) : null,
      minOrderAmount: form.minOrderAmount ? Number(form.minOrderAmount) : 0,
      validFrom: form.validFrom ? `${form.validFrom}:00` : null,
      validUntil: form.validUntil ? `${form.validUntil}:00` : null,
      totalQuantity: form.totalQuantity ? Number(form.totalQuantity) : null,
      targetType: form.targetType,
      targetIds,
      maxUsagePerCustomer: Number(form.maxUsagePerCustomer) || 1,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.couponCode.trim() || !form.name.trim()) {
      showSnackbar('쿠폰 코드와 이름은 필수입니다.', 'error');
      return;
    }
    if (!form.discountValue || Number(form.discountValue) <= 0) {
      showSnackbar('할인 값은 0보다 커야 합니다.', 'error');
      return;
    }
    if (!form.validFrom || !form.validUntil) {
      showSnackbar('유효기간을 입력해주세요.', 'error');
      return;
    }
    if (new Date(form.validUntil) < new Date(form.validFrom)) {
      showSnackbar('만료일이 시작일보다 빠를 수 없습니다.', 'error');
      return;
    }
    if (form.targetType !== 'ALL' && (!form.targetItems || form.targetItems.length === 0)) {
      const message = form.targetType === 'SPECIFIC_STORES'
        ? '대상 매장을 1개 이상 선택해주세요.'
        : '발급 대상을 1명 이상 선택해주세요.';
      showSnackbar(message, 'error');
      return;
    }
    if (form.targetType === 'ALL' || form.targetType === 'SPECIFIC_STORES') {
      const qty = Number(form.maxUsagePerCustomer) || 1;
      const scope = form.targetType === 'ALL'
        ? '전체 활성 회원'
        : `선택한 ${form.targetItems.length}개 매장 한정 쿠폰을 전체 활성 회원`;
      const confirmMsg = `${scope}에게 1인 ${qty}개씩 쿠폰이 발급됩니다.\n회원 수에 따라 응답이 다소 지연될 수 있어요.\n\n계속 진행할까요?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }
    setSubmitting(true);
    try {
      const result = await createCoupon(buildPayload());
      showSnackbar(`쿠폰 [${result?.data?.couponCode}] 발급 완료!`, 'success');
      setForm(initialForm);
      setCreateOpen(false);
      fetchCoupons();
    } catch (error) {
      showSnackbar(error.message || '쿠폰 발급에 실패했습니다.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-layout">
      <Header />
      <PageHeader title="쿠폰 관리" />
      <div className="coupon-management">
        <div className="main-content">
          <div className="coupon-toolbar">
            <input
              type="text"
              className="search-input"
              placeholder="쿠폰 이름, 쿠폰 코드로 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn-search" onClick={handleSearch}>검색</button>
            <div className="toolbar-spacer" />
            <span className="total-count">총 {pagination.totalElements}개</span>
            <button className="btn-primary" onClick={openCreate}>쿠폰 발급</button>
          </div>

          <div className="coupon-table-wrap">
            <table className="coupon-table">
              <thead>
                <tr>
                  <th>쿠폰이름</th>
                  <th>발급대상</th>
                  <th>쿠폰유형</th>
                  <th>쿠폰발급일자</th>
                  <th>유효기간</th>
                  <th>발급/사용</th>
                  <th>상태</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="empty-row">불러오는 중...</td></tr>
                ) : coupons.length === 0 ? (
                  <tr><td colSpan={8} className="empty-row">쿠폰이 없습니다</td></tr>
                ) : (
                  coupons.map((c) => (
                    <tr key={c.couponId}>
                      <td>
                        <div className="coupon-name">{c.name}</div>
                        <div className="coupon-code">{c.couponCode}</div>
                      </td>
                      <td>{getTargetTypeLabel(c.targetType)}</td>
                      <td>
                        <span className={`badge ${c.discountType === 'PERCENT' ? 'badge-percent' : 'badge-amount'}`}>
                          {getDiscountTypeLabel(c.discountType)}
                        </span>
                      </td>
                      <td>{formatDate(c.createdAt)}</td>
                      <td>
                        {formatDate(c.validFrom)}<br />~ {formatDate(c.validUntil)}
                      </td>
                      <td>{c.issuedQuantity ?? 0} / {c.totalQuantity ?? '∞'}</td>
                      <td>
                        <span className={`badge ${c.isActive ? 'badge-active' : 'badge-inactive'}`}>
                          {c.isActive ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td>
                        <button className="btn-text" onClick={() => openDetail(c)}>상세</button>
                        <button className="btn-text" onClick={() => handleToggleActive(c)}>
                          {c.isActive ? '비활성화' : '활성화'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => handlePage(pagination.page - 1)} disabled={pagination.page === 0}>이전</button>
              <span>{pagination.page + 1} / {pagination.totalPages}</span>
              <button onClick={() => handlePage(pagination.page + 1)} disabled={pagination.page + 1 >= pagination.totalPages}>다음</button>
            </div>
          )}
        </div>
      </div>

      {createOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setCreateOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>쿠폰 발급</h2>
              <button className="modal-close" onClick={() => !submitting && setCreateOpen(false)}>×</button>
            </div>
            <form className="coupon-form" onSubmit={handleSubmit}>
              <section className="form-section">
                <h3>기본 정보</h3>
                <div className="form-row">
                  <label>쿠폰 코드 *
                    <input type="text" value={form.couponCode} onChange={(e) => handleChange('couponCode', e.target.value)} placeholder="예: WELCOME2026" maxLength={30} required disabled={submitting} />
                  </label>
                  <label>쿠폰 이름 *
                    <input type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="예: 신규 가입 5천원 쿠폰" maxLength={100} required disabled={submitting} />
                  </label>
                </div>
                <label>설명
                  <textarea value={form.description} onChange={(e) => handleChange('description', e.target.value)} rows={2} disabled={submitting} />
                </label>
                <div className="form-row">
                  <label>쿠폰 타입
                    <select value={form.couponType} onChange={(e) => handleChange('couponType', e.target.value)} disabled={submitting}>
                      {getCouponTypeOptions().map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </label>
                </div>
              </section>

              <section className="form-section">
                <h3>할인 조건</h3>
                <div className="form-row">
                  <label>할인 방식
                    <select value={form.discountType} onChange={(e) => handleChange('discountType', e.target.value)} disabled={submitting}>
                      {getDiscountTypeOptions().map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </label>
                  <label>{form.discountType === 'PERCENT' ? '할인율 (%)' : '할인 금액 (원)'} *
                    <input type="number" min="0" step={form.discountType === 'PERCENT' ? '1' : '100'} value={form.discountValue} onChange={(e) => handleChange('discountValue', e.target.value)} required disabled={submitting} />
                  </label>
                </div>
                <div className="form-row">
                  <label>최대 할인 금액 (원)
                    <input type="number" min="0" value={form.maxDiscountAmount} onChange={(e) => handleChange('maxDiscountAmount', e.target.value)} placeholder="정률 할인 시 권장" disabled={submitting} />
                  </label>
                  <label>최소 주문 금액 (원)
                    <input type="number" min="0" value={form.minOrderAmount} onChange={(e) => handleChange('minOrderAmount', e.target.value)} disabled={submitting} />
                  </label>
                </div>
              </section>

              <section className="form-section">
                <h3>유효기간 및 발급량</h3>
                <div className="form-row">
                  <label>유효 시작 *
                    <input type="datetime-local" value={form.validFrom} onChange={(e) => handleChange('validFrom', e.target.value)} required disabled={submitting} />
                  </label>
                  <label>유효 만료 *
                    <input type="datetime-local" value={form.validUntil} onChange={(e) => handleChange('validUntil', e.target.value)} required disabled={submitting} />
                  </label>
                </div>
                <div className="form-row">
                  <label>총 발급 수량 (선택)
                    <input type="number" min="1" value={form.totalQuantity} onChange={(e) => handleChange('totalQuantity', e.target.value)} placeholder="무제한이면 비워두세요" disabled={submitting} />
                  </label>
                  <label>1인 발급 개수
                    <input type="number" min="1" value={form.maxUsagePerCustomer} onChange={(e) => handleChange('maxUsagePerCustomer', e.target.value)} placeholder="대상자 1명당 발급할 개수" disabled={submitting} />
                  </label>
                </div>
              </section>

              <section className="form-section">
                <h3>발급 대상</h3>
                <div className="form-row">
                  <label>대상 타입
                    <select
                      value={form.targetType}
                      onChange={(e) => {
                        handleChange('targetType', e.target.value);
                        handleChange('targetItems', []);  // 타입 바꿀 때 선택 초기화
                      }}
                      disabled={submitting}
                    >
                      {getTargetTypeOptions().map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </label>
                </div>
                {form.targetType === 'SPECIFIC_CUSTOMERS' && (
                  <div className="target-selector-wrap">
                    <span className="target-label">대상 고객 선택 ({form.targetItems.length}명)</span>
                    <TargetSelector
                      type="CUSTOMER"
                      value={form.targetItems}
                      onChange={(items) => handleChange('targetItems', items)}
                      disabled={submitting}
                    />
                  </div>
                )}
                {form.targetType === 'SPECIFIC_STORES' && (
                  <div className="target-selector-wrap">
                    <span className="target-label">대상 매장 선택 ({form.targetItems.length}개)</span>
                    <TargetSelector
                      type="STORE"
                      value={form.targetItems}
                      onChange={(items) => handleChange('targetItems', items)}
                      disabled={submitting}
                    />
                  </div>
                )}
              </section>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)} disabled={submitting}>취소</button>
                <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? '발급중...' : '쿠폰 발급'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detailOpen && (
        <div className="modal-overlay" onClick={() => setDetailOpen(false)}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>쿠폰 상세</h2>
              <button className="modal-close" onClick={() => setDetailOpen(false)}>×</button>
            </div>
            <div className="detail-body">
              {detailLoading ? (
                <p className="detail-loading">불러오는 중...</p>
              ) : !detail ? (
                <p className="detail-loading">상세 정보를 불러올 수 없습니다.</p>
              ) : (
                <>
                  <section className="detail-section">
                    <h3>기본 정보</h3>
                    <dl className="detail-list">
                      <div><dt>쿠폰 코드</dt><dd className="mono">{detail.couponCode}</dd></div>
                      <div><dt>쿠폰 이름</dt><dd>{detail.name}</dd></div>
                      <div><dt>설명</dt><dd>{detail.description || '-'}</dd></div>
                      <div><dt>쿠폰 타입</dt><dd>{getCouponTypeLabel(detail.couponType)}</dd></div>
                      <div><dt>상태</dt><dd>
                        <span className={`badge ${detail.isActive ? 'badge-active' : 'badge-inactive'}`}>
                          {detail.isActive ? '활성' : '비활성'}
                        </span>
                      </dd></div>
                    </dl>
                  </section>

                  <section className="detail-section">
                    <h3>할인 조건</h3>
                    <dl className="detail-list">
                      <div><dt>할인 방식</dt><dd>
                        <span className={`badge ${detail.discountType === 'PERCENT' ? 'badge-percent' : 'badge-amount'}`}>
                          {getDiscountTypeLabel(detail.discountType)}
                        </span>
                      </dd></div>
                      <div><dt>할인 값</dt><dd>
                        {detail.discountType === 'PERCENT'
                          ? `${detail.discountValue}%`
                          : `${Number(detail.discountValue).toLocaleString()}원`}
                      </dd></div>
                      <div><dt>최대 할인 금액</dt><dd>
                        {detail.maxDiscountAmount ? `${Number(detail.maxDiscountAmount).toLocaleString()}원` : '없음'}
                      </dd></div>
                      <div><dt>최소 주문 금액</dt><dd>
                        {detail.minOrderAmount ? `${Number(detail.minOrderAmount).toLocaleString()}원` : '없음'}
                      </dd></div>
                    </dl>
                  </section>

                  <section className="detail-section">
                    <h3>유효기간 / 발급량</h3>
                    <dl className="detail-list">
                      <div><dt>유효 시작</dt><dd>{formatDateTime(detail.validFrom)}</dd></div>
                      <div><dt>유효 만료</dt><dd>{formatDateTime(detail.validUntil)}</dd></div>
                      <div><dt>총 발급 수량</dt><dd>{detail.totalQuantity ?? '무제한'}</dd></div>
                      <div><dt>발급된 수량</dt><dd>{detail.issuedQuantity ?? 0}</dd></div>
                      <div><dt>사용된 수량</dt><dd>{detail.usedQuantity ?? 0}</dd></div>
                      <div><dt>1인 발급 개수</dt><dd>{detail.maxUsagePerCustomer ?? 1}개</dd></div>
                    </dl>
                  </section>

                  <section className="detail-section">
                    <h3>발급 대상</h3>
                    <dl className="detail-list">
                      <div><dt>대상 타입</dt><dd>{getTargetTypeLabel(detail.targetType)}</dd></div>
                      {detail.targetType !== 'ALL' && (
                        <div><dt>{detail.targetType === 'SPECIFIC_STORES' ? '대상 매장' : '대상 고객'}</dt><dd>
                          {detail.targetDetails && detail.targetDetails.length > 0 ? (
                            <ul className="target-detail-list">
                              {detail.targetDetails.map((t) => (
                                <li key={t.id}>
                                  <span className="target-detail-label">{t.label}</span>
                                  {t.sub && <span className="target-detail-sub">{t.sub}</span>}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="mono target-ids">
                              {detail.targetIds && detail.targetIds.length > 0
                                ? detail.targetIds.join(', ')
                                : '-'}
                            </span>
                          )}
                        </dd></div>
                      )}
                    </dl>
                  </section>

                  <section className="detail-section">
                    <h3>이력</h3>
                    <dl className="detail-list">
                      <div><dt>생성일</dt><dd>{formatDateTime(detail.createdAt)}</dd></div>
                      <div><dt>최종 수정</dt><dd>{formatDateTime(detail.updatedAt)}</dd></div>
                    </dl>
                  </section>
                </>
              )}
            </div>
            {detail && !detailLoading && (
              <div className="detail-footer">
                <button type="button" className="btn-danger" onClick={handleDeleteCoupon}>
                  쿠폰 삭제
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Snackbar message={snackbar.message} type={snackbar.type} isOpen={snackbar.isOpen} onClose={hideSnackbar} />
    </div>
  );
};

export default CouponManagement;